// Clocks for the pictures: an idle clock that ticks at 24 fps, and episode playback locked to the audio.
import { useCallback, useEffect, useRef, useState } from "react";
import { engine, type UrlOf } from "../../audio/playback";
import { lineAudioClips } from "../../audio/timelineAudio";
import { FPS, type Timeline } from "../../engine/timeline";
import type { Library } from "../../model/library";
import { duration, type TimelineEpisode } from "../../model/timeline";
import { voiceOf } from "../../library/store";

// Ticks at 24 fps while active; returns seconds since mount.
export function useIdleClock(active = true) {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t0 = performance.now() - t * 1000;
    let raf = 0, last = -1;
    const loop = () => {
      const now = (performance.now() - t0) / 1000;
      const f = Math.floor(now * FPS);
      if (f !== last) { last = f; setT(now); }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return t;
}

// Plays a stretch of the episode through the audio engine; t is episode time, or null when stopped.
export function usePlayback(tl: Timeline, lib: Library, urlOf: UrlOf) {
  const [t, setT] = useState<number | null>(null);
  const gen = useRef(0);
  const ref = useRef({ tl, lib, urlOf });
  ref.current = { tl, lib, urlOf };
  const stop = useCallback(() => {
    gen.current++;
    engine.stop();
    setT(null);
  }, []);
  const play = useCallback(async (from: number, to: number, onEnd?: () => void) => {
    const my = ++gen.current;
    const { tl, lib, urlOf } = ref.current;
    const now = await engine.start(tl, from, to, urlOf, voiceOf(lib));
    if (my !== gen.current) return;
    let last = -1;
    const loop = () => {
      if (my !== gen.current) return;
      const tt = now();
      if (tt >= to) {
        gen.current++;
        setT(null);
        onEnd?.();
        return;
      }
      const f = Math.floor(tt * FPS);
      if (f !== last) { last = f; setT(tt); }
      requestAnimationFrame(loop);
    };
    loop();
  }, []);
  useEffect(() => () => { gen.current++; engine.stop(); }, []);
  return { t, play, stop, playing: t !== null };
}

// Plays a timeline (v6) episode from `from` to its end through the audio engine, every spoken line at its start through
// its speaker's voice (lines come one after another). `t` is episode time, or null when stopped. `onEnd` runs when it reaches the end,
// which is the episode's end unless `until` stops it sooner (Quick view plays one scene).
export function useTimelinePlayback(ep: TimelineEpisode, lib: Library) {
  const [t, setT] = useState<number | null>(null);
  const gen = useRef(0);
  const ref = useRef({ ep, lib });
  ref.current = { ep, lib };
  const stop = useCallback(() => {
    gen.current++;
    engine.stop();
    setT(null);
  }, []);
  const play = useCallback(async (from: number, onEnd?: () => void, until?: number) => {
    const my = ++gen.current;
    const { ep, lib } = ref.current;
    const to = Math.min(duration(ep), until ?? Infinity);
    const clips = lineAudioClips(ep, file => `/data/v2/episodes/${ep.id}/${file}`);
    const now = await engine.startClips(clips, from, to, voiceOf(lib));
    if (my !== gen.current) return;
    let last = -1;
    const loop = () => {
      if (my !== gen.current) return;
      const tt = now();
      if (tt >= to) {
        gen.current++;
        engine.stop();
        setT(null);
        onEnd?.();
        return;
      }
      const f = Math.floor(tt * FPS);
      if (f !== last) { last = f; setT(tt); }
      requestAnimationFrame(loop);
    };
    loop();
  }, []);
  useEffect(() => () => { gen.current++; engine.stop(); }, []);
  return { t, play, stop, playing: t !== null };
}
